import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, MapPin, Star, Calendar, Package, CheckCircle2, MessageCircle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ProductCard } from "@/components/ProductCard";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function StoreFront() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [sold, setSold] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get(`/stores/${slug}`).then(({ data }) => setStore(data)).catch(() => setStore(false));
    api.get(`/stores/${slug}/products?tab=active`).then(({ data }) => setProducts(data)).catch(() => {});
    api.get(`/stores/${slug}/products?tab=sold`).then(({ data }) => setSold(data)).catch(() => {});
    api.get(`/stores/${slug}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  }, [slug]);

  if (store === null) return <Layout><div className="py-24 text-center text-rl-muted">Memuat toko...</div></Layout>;
  if (store === false) return <Layout><div className="py-24 text-center text-rl-muted">Toko tidak ditemukan.</div></Layout>;

  const startChat = () => {
    if (!user) return navigate("/login", { state: { from: `/toko/${slug}` } });
    navigate("/chat", { state: { start: { store_id: store.id } } });
  };

  return (
    <Layout>
      {/* Store header */}
      <div className="bg-rl-bg2 border-b border-rl-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-rl-secondary text-white grid place-items-center font-serif text-3xl font-bold">
              {store.name?.[0]}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-3xl font-bold text-rl-ink">{store.name}</h1>
                {store.verified && <span className="inline-flex items-center gap-1 rounded-full bg-rl-secondary/10 text-rl-secondary px-2.5 py-0.5 text-xs font-semibold"><ShieldCheck className="w-3.5 h-3.5" />Verified Seller</span>}
                <span className="rounded-full bg-white border border-rl-border px-2.5 py-0.5 text-xs font-semibold text-rl-muted capitalize">{store.seller_type === "business" ? "Bisnis" : "Perorangan"}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-rl-ink2">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-rl-muted" />{store.location}</span>
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-rl-muted" />Bergabung {fmtDate(store.joined)}</span>
              </div>
              {store.description && <p className="mt-3 text-sm text-rl-ink2 max-w-2xl">{store.description}</p>}
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <Stat icon={Star} label="Rating" value={store.rating || "Baru"} />
              <Stat icon={CheckCircle2} label="Terjual" value={store.completed_sales || 0} />
              <Stat icon={Package} label="Listing" value={store.active_listings || 0} />
            </div>
          </div>
          <button data-testid="store-chat-btn" onClick={startChat}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-rl-secondary text-rl-secondary hover:bg-rl-secondary hover:text-white font-semibold px-5 py-2.5 transition-colors">
            <MessageCircle className="w-4 h-4" />Chat Toko
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <Tabs defaultValue="produk">
          <TabsList data-testid="store-tabs">
            <TabsTrigger value="produk" data-testid="tab-produk">Produk ({products.length})</TabsTrigger>
            <TabsTrigger value="terjual" data-testid="tab-terjual">Terjual ({sold.length})</TabsTrigger>
            <TabsTrigger value="ulasan" data-testid="tab-ulasan">Ulasan ({reviews.length})</TabsTrigger>
            <TabsTrigger value="tentang" data-testid="tab-tentang">Tentang Toko</TabsTrigger>
          </TabsList>

          <TabsContent value="produk" className="mt-6">
            {products.length === 0 ? <p className="text-rl-muted py-10 text-center">Belum ada produk aktif.</p> :
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">{products.map((p) => <ProductCard key={p.id} p={{ ...p, store }} />)}</div>}
          </TabsContent>
          <TabsContent value="terjual" className="mt-6">
            {sold.length === 0 ? <p className="text-rl-muted py-10 text-center">Belum ada penjualan.</p> :
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 opacity-70">{sold.map((p) => <ProductCard key={p.id} p={{ ...p, store }} />)}</div>}
          </TabsContent>
          <TabsContent value="ulasan" className="mt-6">
            {reviews.length === 0 ? <p className="text-rl-muted py-10 text-center">Belum ada ulasan.</p> :
              <div className="grid md:grid-cols-2 gap-4">{reviews.map((r) => (
                <div key={r.id} className="rounded-xl border border-rl-border bg-white p-4">
                  <div className="flex items-center gap-2"><span className="font-semibold">{r.buyer_name}</span>
                    <span className="flex text-rl-gold">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-rl-gold" />)}</span>
                    <span className="text-xs text-rl-muted ml-auto">{fmtDate(r.created_at)}</span></div>
                  <p className="mt-2 text-sm text-rl-ink2">{r.comment}</p>
                  <p className="mt-1 text-xs text-rl-muted">{r.product_title}</p>
                </div>))}</div>}
          </TabsContent>
          <TabsContent value="tentang" className="mt-6">
            <div className="rounded-2xl border border-rl-border bg-white p-6 max-w-2xl">
              <h3 className="font-bold text-rl-ink">Tentang {store.name}</h3>
              <p className="mt-2 text-sm text-rl-ink2">{store.description || "Tidak ada deskripsi."}</p>
              <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
                <dt className="text-rl-muted">Tipe Seller</dt><dd className="font-semibold capitalize">{store.seller_type === "business" ? "Bisnis" : "Perorangan"}</dd>
                <dt className="text-rl-muted">Lokasi</dt><dd className="font-semibold">{store.location}</dd>
                <dt className="text-rl-muted">Bergabung</dt><dd className="font-semibold">{fmtDate(store.joined)}</dd>
                <dt className="text-rl-muted">Status</dt><dd className="font-semibold">{store.verified ? "Terverifikasi" : "Belum diverifikasi"}</dd>
              </dl>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-white border border-rl-border px-4 py-3 min-w-[80px]">
    <Icon className="w-4 h-4 text-rl-primary mx-auto" />
    <div className="mt-1 font-bold text-rl-ink">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-rl-muted">{label}</div>
  </div>
);
